/**
 * Pure region-selection logic shared by the `/assets` World viewer (lists
 * all six baked regions) and `/fly` (picks the mission's own region + start
 * aerodrome to load) — no fetch, no DOM, unit-testable directly against a
 * manifest's `entries` array.
 */

import type { AssetCategory, BakeManifestEntry, WorldMeta } from "@red-opus/baker/schema";
import { MISSION_REGIONS, type MissionRegion } from "@red-opus/engine";

export interface WorldRegionSummary {
  readonly id: string;
  readonly path: string;
  readonly region: string;
  readonly placementCount: number;
  readonly aerodromeCount: number;
}

const WORLD_CATEGORY: AssetCategory = "world";

/** Every baked `world` entry, summarised and sorted by display region name. */
export function worldRegionEntries(entries: readonly BakeManifestEntry[]): WorldRegionSummary[] {
  return entries
    .filter((entry) => entry.category === WORLD_CATEGORY)
    .map((entry) => {
      const meta = entry.meta as WorldMeta;
      return {
        id: entry.id,
        path: entry.path,
        region: meta.region,
        placementCount: meta.placementCount,
        aerodromeCount: meta.aerodromeCount,
      };
    })
    .sort((a, b) => a.region.localeCompare(b.region));
}

/** Looks up one manifest entry by its `world/<ID>` manifest id. */
export function findWorldEntry(entries: readonly BakeManifestEntry[], id: string): BakeManifestEntry | undefined {
  return entries.find((entry) => entry.category === WORLD_CATEGORY && entry.id === id);
}

/** One `MissionRegion`'s baked world file and the aerodrome `/fly` recentres its scenery on. */
export interface RegionBakeEntry {
  readonly id: string;
  readonly path: string;
  readonly startAerodrome: string;
}

/**
 * Every `MissionRegion`'s baked `.WLD` region and start aerodrome
 * (docs/formats/world-terrain.md's scale-inference addendum for the method).
 *
 * `.WLD` `x`/`y` never resolved to `AERO.DAT` `mapX`/`mapY` at the byte level
 * (world-terrain.md §2.4), so the *positions* are fitted:
 * `aerodrome-projection.ts` stretches a region's own aerodromes onto its
 * placement bounding box and `region-ground.ts` anchors the named start field
 * onto the nearest scenery placement with five dry kilometres round it. Which
 * field gets named is this table, and it is not a claim about which nation held
 * the ground: the heuristic has no way to know, and Verdun's St. Mihiel was
 * German-held (`AERODROMES` in `packages/engine/src/campaign/aerodromes.ts`).
 * The recentre point is a scenery anchor, not an operational statement.
 *
 * ## The criterion (G74, 2026-09-01) — neighbourhood density, not proximity
 *
 * The first version of this table picked, per region, the field that landed
 * **nearest *a* scenery placement**. That optimises the wrong thing, and two
 * regions paid for it: a lone farmhouse on the edge of an empty quarter scores
 * a perfect zero on "nearest placement" and is the emptiest spawn in the map.
 * Courtrai and St. Quentin were both anchored *onto* a placement — distance
 * 0 m — with **no building at all within ten kilometres**, which is what the
 * user flew and reported as barren country.
 *
 * So the criterion is now the neighbourhood, measured at the point `/fly`
 * actually recentres on:
 *
 * 1. **Eligibility** is unchanged and still `anchorSite`'s: the field must
 *    anchor onto a scenery site whose `MISSION_GROUND_RADIUS_M` (5 km) disc is
 *    dry, ranked dry-share first and distance second. Nothing here weakens the
 *    spawn/belt/ring dryness `london-sea-placements.test.ts` pins.
 * 2. **Score** is `near10` — the count of *placed structures* (the `roofM > 0`
 *    census `world/scenery-structures.ts` already owns, i.e. buildings, not
 *    hills and not ground plates) within 10 km of the anchored origin. Ten
 *    kilometres because that is the ground a sortie is over in its first
 *    minutes; structures because "is there a town here" is the percept, and a
 *    100 km terrain plate is not one.
 * 3. **Floor and hysteresis.** An incumbent scoring `>= 5` keeps its seat; only
 *    a field below the floor is replaced, by the eligible field with the
 *    greatest `near10` (ties: `near20`, then the smaller anchor displacement,
 *    then name). The floor is hysteresis, not a taste flag: re-picking a seat
 *    invalidates every tape flown from it (`SPAWN_LAYOUT_VERSION`), and the
 *    projection's own error is kilometres wide, so a few buildings of margin is
 *    not evidence of anything. **The answer does not depend on the exact
 *    floor** — the corpus is bimodal (the two failures score 0 and 0, the three
 *    that pass score 12, 17 and 18) and every floor from 1 to 12 produces this
 *    same table. `spawn-neighbourhood.test.ts` asserts that band against the
 *    real bake, so "5" cannot quietly become load-bearing.
 *
 * ## Before / after, measured against the shipped bake
 *
 * `near10`/`near20` are structures within 10/20 km of the anchored origin;
 * `moved` is how far anchoring displaced the fit (`anchoredByM`).
 *
 * | region  | was (near10 / near20 / moved) | now (near10 / near20 / moved) |
 * |---------|-------------------------------|-------------------------------|
 * | london  | Hainault Farm — 18 / 37 / 9.5 km | **unchanged** |
 * | dunkirk | Courtrai — **0 / 0** / 3.6 km | **St. Marie Cappel** — 24 / 68 / 4.8 km |
 * | somme   | Cachy — 12 / 13 / 1.7 km | **unchanged** |
 * | paris   | St. Quentin — **0 / 11** / 0.6 km | **Villers-Cotterets** — 7 / 57 / 1.8 km |
 * | verdun  | St. Mihiel — 17 / 38 / 1.05 km | **unchanged** |
 *
 * Three notes on the two that moved, because neither is the field the roadmap
 * entry guessed at and the difference is the criterion talking:
 *
 * - **Dunkirk goes to St. Marie Cappel, not La Gorgue.** La Gorgue scores 6
 *   structures within 10 km against St. Marie Cappel's 24, and the fit has to
 *   be dragged **25.5 km** to anchor La Gorgue against 4.8 km for St. Marie
 *   Cappel. It is not close under any variant either: measured at the *bare*
 *   projection before anchoring, St. Marie Cappel has 35 placements within
 *   10 km and La Gorgue has 1. (St. Marie Cappel was this table's own recorded
 *   runner-up all along.)
 * - **Paris's winner is thin in absolute terms** — 7 structures within 10 km,
 *   nothing at all within 5 km — because *every* Paris field is: the region's
 *   191 structures sit in clusters no projected aerodrome lands inside. Seven
 *   within 10 km and 57 within 20 is the best the region has, against
 *   St. Quentin's 0 and 11.
 * - **Verdun stays on the floor rule, not on a tie-break.** Metz matches
 *   St. Mihiel's 17 within 10 km and beats it at 20 km (74 vs 38). St. Mihiel
 *   clears the floor, so it keeps the seat and the tapes flown from it keep
 *   whatever else about them still verifies — exactly what the hysteresis is
 *   for. The same rule keeps London on Hainault Farm (18) over Ashford (23).
 *
 * Moving a seat **invalidates every tape recorded from it**, which the user
 * accepted once, deliberately, rather than build a migration layer ("we can
 * invalidate tapes", 2026-09-01). The mechanism is `SPAWN_LAYOUT_VERSION` in
 * `packages/engine/src/replay/replay-tape.ts`; changing this table without
 * bumping it fails `spawn-neighbourhood.test.ts`.
 */
export const REGION_BAKE: Readonly<Record<MissionRegion, RegionBakeEntry>> = {
  london: { id: "world/LONDON", path: "world/LONDON.json", startAerodrome: "Hainault Farm" },
  dunkirk: { id: "world/DUNKIRK", path: "world/DUNKIRK.json", startAerodrome: "St. Marie Cappel" },
  somme: { id: "world/SOMME", path: "world/SOMME.json", startAerodrome: "Cachy" },
  paris: { id: "world/PARIS", path: "world/PARIS.json", startAerodrome: "Villers-Cotterets" },
  verdun: { id: "world/VERDUN", path: "world/VERDUN.json", startAerodrome: "St. Mihiel" },
};

/**
 * `/fly`'s region when no mission names one — the instant dogfight, which has
 * no `MissionSpec` and so no briefed region. Verdun, unchanged from before
 * per-mission regions existed, so every dogfight link and saved tape flown
 * before this lane still shows the same sector.
 */
export const DEFAULT_FLY_REGION: MissionRegion = "verdun";

/** `REGION_BAKE[DEFAULT_FLY_REGION]`'s manifest id — kept as a named export for existing callers/tests. */
export const FLY_REGION_ID = REGION_BAKE[DEFAULT_FLY_REGION].id;
/** The manifest path for `FLY_REGION_ID` (`region-loader.ts` fetches it directly — `/fly` doesn't load the manifest, matching `flight-scene.ts`'s existing hardcoded `PLAYER_MODEL_PATH`). */
export const FLY_REGION_PATH = REGION_BAKE[DEFAULT_FLY_REGION].path;
export const FLY_START_AERODROME = REGION_BAKE[DEFAULT_FLY_REGION].startAerodrome;

/** Every `MissionRegion` has a `REGION_BAKE` entry — asserted once at module load rather than left to a missing-key `undefined` surfacing as a runtime fetch 404 deep in `/fly`. */
function assertAllRegionsBaked(): void {
  for (const region of MISSION_REGIONS) {
    if (!REGION_BAKE[region]) throw new Error(`region-catalog: no REGION_BAKE entry for mission region "${region}"`);
  }
}
assertAllRegionsBaked();

/** The baked world entry a `MissionSpec.region` (or `undefined`, for the instant dogfight) should load. */
export function regionBakeFor(region: MissionRegion | undefined): RegionBakeEntry {
  return REGION_BAKE[region ?? DEFAULT_FLY_REGION];
}
